import { useState, useEffect } from 'react';
import { 
  DndContext, 
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useTranslation } from '../../hooks/useTranslation';
import * as Icons from 'lucide-react';
import toast from 'react-hot-toast';
import { createPortal } from 'react-dom';

function SortableItem({ id, data }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`
        flex items-center gap-2 bg-white py-2 px-3 rounded shadow-sm border 
        ${isDragging ? 'border-indigo-400 shadow-lg' : 'border-gray-200'} 
        max-w-xl transition-shadow cursor-grab active:cursor-grabbing
      `}
    >
      <Icons.GripVertical className="h-4 w-4 text-gray-400 flex-shrink-0" />
      <div className="h-8 w-8 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center text-sm font-medium text-gray-600 flex-shrink-0">
        {data.username?.charAt(0).toUpperCase()}
      </div>
      <div className="min-w-0 flex-1 grid grid-cols-2 gap-x-4">
        <div>
          <div className="text-sm font-medium text-gray-900 truncate">
            {data.username}
          </div>
          <div className="text-xs text-gray-500">ID: {data.id}</div>
        </div>
        <div>
          <div className="text-sm text-gray-600 truncate">
            {data.full_name || data.name || '-'}
          </div>
          <div className="text-xs text-gray-500 truncate">
            {data.email || '-'}
          </div>
        </div>
      </div>
    </div>
  );
}

function DragOverlayItem({ data }) {
  return (
    <div className="flex items-center gap-2 bg-white py-2 px-3 rounded shadow-lg border border-indigo-400 max-w-xl">
      <Icons.GripVertical className="h-4 w-4 text-gray-400 flex-shrink-0" />
      <div className="h-8 w-8 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center text-sm font-medium text-gray-600 flex-shrink-0">
        {data.username?.charAt(0).toUpperCase()}
      </div>
      <div className="min-w-0 flex-1 grid grid-cols-2 gap-x-4">
        <div>
          <div className="text-sm font-medium text-gray-900 truncate">
            {data.username}
          </div>
          <div className="text-xs text-gray-500">ID: {data.id}</div>
        </div>
        <div>
          <div className="text-sm text-gray-600 truncate">
            {data.full_name || data.name || '-'}
          </div>
          <div className="text-xs text-gray-500 truncate">
            {data.email || '-'}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ReferralTreeEditor() {
  const { t } = useTranslation();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const customersRef = collection(db, 'customers');
        const snapshot = await getDocs(customersRef);
        const customersData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setCustomers(customersData);
      } catch (err) {
        console.error('Error fetching customers:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, []);

  const buildTree = (customers) => {
    if (!customers?.length) return [];
    
    const customerMap = new Map();
    customers.forEach(customer => {
      if (customer?.id) {  // Csak akkor adjuk hozzá, ha van ID
        customerMap.set(customer.id, { ...customer, children: [] });
      }
    });

    const tree = [];
    customers.forEach(customer => {
      if (!customer?.id) return;  // Skip ha nincs ID
      
      const node = customerMap.get(customer.id);
      if (!node) return;  // Skip ha nincs node
      
      if (customer.ref_id && customerMap.has(customer.ref_id)) {
        const parent = customerMap.get(customer.ref_id);
        parent.children.push(node);
      } else {
        tree.push(node);
      }
    });

    return tree;
  };

  const renderNode = (node, level = 0) => {
    if (!node?.id) return null;  // Ne rendereljünk invalid node-ot
    
    return (
      <div key={node.id} style={{ marginLeft: `${level * 20}px` }}>
        <div className="flex items-center gap-2 p-2">
          <div className="h-8 w-8 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center text-sm font-medium text-gray-600">
            {node.username ? node.username.charAt(0).toUpperCase() : '?'}
          </div>
          <div>
            <div className="text-sm font-medium text-gray-900">
              {node.username || 'Unknown'}
            </div>
            <div className="text-xs text-gray-500">
              ID: {node.id ? node.id.toString().slice(0, 8) : 'N/A'}
            </div>
          </div>
        </div>
        {node.children?.map(child => renderNode(child, level + 1))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-4">
        <Icons.Loader className="animate-spin h-8 w-8 text-gray-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-500">
        Error: {error}
      </div>
    );
  }

  const tree = buildTree(customers);

  return (
    <div className="p-4">
      <h2 className="text-lg font-semibold mb-4">Referral Tree</h2>
      <div className="bg-white rounded-lg shadow p-4">
        {tree.map(node => renderNode(node))}
      </div>
    </div>
  );
} 